import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";
import { useNavigate } from "react-router-dom";

import {
  CARRIER_UPGRADE_RELATIVE_URL,
  CarrierUpgradeSource,
  ClientAppEvent,
  EVOTRUX_BLUE,
  EVOTRUX_BLUE_DARK,
  EVOTRUX_BLUE_LIGHT,
  EVOTRUX_BLUE_SHADOW,
  EVOTRUX_BLUE_SHADOW_HOVER,
} from "../../../../constants";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { captureCarrierUpgradeCtaClicked } from "../../../../utils";

interface Props {
  publicShipmentCount: number;
}

function PlaceholderShipmentRow() {
  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Box
          sx={{
            height: 14,
            width: "40%",
            bgcolor: "grey.300",
            borderRadius: "4px",
          }}
        />
        <Box
          sx={{
            height: 12,
            width: "70%",
            bgcolor: "grey.200",
            borderRadius: "4px",
          }}
        />
        <Box
          sx={{
            height: 12,
            width: "55%",
            bgcolor: "grey.200",
            borderRadius: "4px",
          }}
        />
      </Stack>
    </Box>
  );
}

interface BodyCopyProps {
  publicShipmentCount: number;
}

function BodyCopy(props: BodyCopyProps) {
  const { publicShipmentCount } = props;

  if (publicShipmentCount <= 1) {
    return <>Upgrade to access more shipments</>;
  }

  return (
    <>
      Subscribe to access more freight. There are{" "}
      <Box component="span" sx={{ fontWeight: 700, fontSize: "16px" }}>
        {publicShipmentCount}
      </Box>{" "}
      shipments available right now!
    </>
  );
}

/**
 * Shown to CARRIER_ESSENTIALS_V2 carriers on the "public shipments" tab. The
 * shipment list is blurred behind an upgrade CTA — V2 carriers cannot access
 * non-partner shipments.
 */
export function PublicShipmentsLockedOverlay(props: Props) {
  const { publicShipmentCount } = props;
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const posthog = usePostHog();

  const onSubscribeClick = () => {
    posthog?.capture(ClientAppEvent.CARRIER_PUBLIC_SHIPMENTS_SUBSCRIBE_CLICKED);
    captureCarrierUpgradeCtaClicked(
      posthog,
      CarrierUpgradeSource.PUBLIC_SHIPMENTS_OVERLAY,
    );
    navigate(CARRIER_UPGRADE_RELATIVE_URL);
  };

  const onLearnMoreClick = () => {
    posthog?.capture(
      ClientAppEvent.CARRIER_PUBLIC_SHIPMENTS_LEARN_MORE_CLICKED,
    );
    navigate(CARRIER_UPGRADE_RELATIVE_URL);
  };

  return (
    <Box sx={{ position: "relative", overflow: "hidden", borderRadius: "6px" }}>
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          userSelect: "none",
          filter: "blur(6px)",
          opacity: 0.4,
        }}
      >
        {[0, 1, 2, 3].map((row) => (
          <Box key={row}>
            <Divider style={{ margin: "0" }} />
            <PlaceholderShipmentRow />
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          minHeight: isMobile ? 320 : 400,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: isMobile ? 4 : 6,
          px: 2,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.03) 0%, rgba(0,0,0,0.15) 100%)",
        }}
      >
        <Stack
          alignItems="center"
          textAlign="center"
          sx={{
            bgcolor: "background.paper",
            borderRadius: "12px",
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
            py: 5,
            px: isMobile ? 3 : 6,
            maxWidth: isMobile ? "none" : 420,
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: EVOTRUX_BLUE_LIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2.5,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 28, color: EVOTRUX_BLUE }} />
          </Box>

          <Typography
            component="h2"
            sx={{
              fontSize: "20px",
              fontWeight: 600,
              color: "text.primary",
              mb: 1,
            }}
          >
            Unlock More Shipments
          </Typography>

          <Typography
            sx={{
              fontSize: "14px",
              color: "text.secondary",
              lineHeight: 1.6,
              mb: 3,
            }}
          >
            <BodyCopy publicShipmentCount={publicShipmentCount} />
          </Typography>

          <Button
            variant="contained"
            onClick={onSubscribeClick}
            sx={{
              bgcolor: EVOTRUX_BLUE,
              fontSize: "14px",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              py: "10px",
              px: 4,
              color: "common.white",
              boxShadow: `0px 2px 8px ${EVOTRUX_BLUE_SHADOW}`,
              "&:hover": {
                bgcolor: EVOTRUX_BLUE_DARK,
                boxShadow: `0px 4px 12px ${EVOTRUX_BLUE_SHADOW_HOVER}`,
              },
            }}
          >
            Subscribe
          </Button>

          <Button
            variant="text"
            onClick={onLearnMoreClick}
            sx={{
              fontSize: "12px",
              color: EVOTRUX_BLUE,
              textTransform: "none",
              mt: 1.5,
              minWidth: 0,
              padding: 0,
              "&:hover": { textDecoration: "underline", background: "none" },
            }}
          >
            Learn more
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
